import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";
import heroImg from "@/assets/images/hero-analytics.jpg";
import dashboardImg from "@/assets/images/dashboard-charts.jpg";
import teamImg from "@/assets/images/team-collaboration.jpg";
import { Check, LineChart, Layers, Sparkles, Compass, Zap, Users } from "lucide-react";

const BENEFIT_ICONS = [LineChart, Layers, Compass, Sparkles, Zap, Users];

export default function Home() {
  const { t, dict } = useI18n();
  const location = useLocation();

  // Handle anchor scrolling when navigating to /#section.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ block: "start" }), 50);
      }
    }
  }, [location.hash, location.pathname]);

  return (
    <div data-testid="home-page" className="text-slate-100">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-4">
              {t("hero.eyebrow")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-white">
              {t("hero.title")}
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-slate-300 max-w-2xl">
              {t("hero.subtitle")}
            </p>

            <ul className="mt-8 space-y-3">
              {(dict.hero.bullets || []).map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-slate-300 text-base leading-relaxed"
                >
                  <Check className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
              >
                {t("hero.cta")}
              </a>
              <a
                href="#how"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center rounded-md border border-slate-700 hover:border-slate-500 text-slate-100 font-semibold px-6 py-3 transition-colors"
              >
                {t("hero.secondary")}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 border-t border-slate-800 pt-6">
              <TrustItem
                value={t("hero.trust.usersValue")}
                label={t("hero.trust.usersLabel")}
              />
              <TrustItem
                value={t("hero.trust.sourcesValue")}
                label={t("hero.trust.sourcesLabel")}
              />
              <TrustItem
                value={t("hero.trust.sectorsValue")}
                label={t("hero.trust.sectorsLabel")}
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
              <img
                src={heroImg}
                alt={t("hero.imageAlt")}
                className="w-full h-auto block"
                loading="eager"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-slate-950/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src={dashboardImg}
              alt={t("about.imageAlt")}
              className="w-full h-auto rounded-2xl border border-slate-800 shadow-xl"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">
              {t("about.eyebrow")}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("about.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-300">{t("about.subtitle")}</p>
            <ul className="mt-6 space-y-3">
              {(dict.about.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300 leading-relaxed">
                  <Check className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">
              {t("how.eyebrow")}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("how.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-300">{t("how.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {(dict.how.steps || []).map((step, i) => (
              <div
                key={i}
                data-testid={`how-step-${i + 1}`}
                className="bg-slate-900 border border-slate-800 rounded-xl p-6"
              >
                <div className="text-4xl font-extrabold text-blue-500/80 tracking-tight mb-3">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">
              {t("benefits.eyebrow")}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("benefits.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-300">{t("benefits.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(dict.benefits.items || []).map((item, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div
                  key={i}
                  data-testid={`benefit-item-${i + 1}`}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-6"
                >
                  <div className="h-10 w-10 flex items-center justify-center rounded-md bg-blue-600/10 border border-blue-600/30 mb-4">
                    <Icon className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">
              {t("newsletter.eyebrow")}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("newsletter.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-300">
              {t("newsletter.subtitle")}
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              {t("newsletter.description")}
            </p>
            <div className="mt-8 rounded-xl overflow-hidden border border-slate-800">
              <img
                src={teamImg}
                alt={t("about.imageAlt")}
                className="w-full h-48 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">
            {t("faq.eyebrow")}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            {t("faq.title")}
          </h2>
          <div className="mt-10 space-y-3">
            {(dict.faq.items || []).map((item, i) => (
              <FaqItem key={i} index={i + 1} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function TrustItem({ value, label }) {
  return (
    <div>
      <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
        {value}
      </div>
      <div className="text-xs text-slate-400 mt-1 leading-snug">{label}</div>
    </div>
  );
}

function FaqItem({ index, q, a }) {
  const [open, setOpen] = React.useState(index === 1);
  return (
    <details
      data-testid={`faq-item-${index}`}
      className="group bg-slate-900 border border-slate-800 rounded-lg"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none text-white font-medium">
        <span>{q}</span>
        <span className="text-blue-400 font-mono text-sm ml-4">
          {open ? "—" : "+"}
        </span>
      </summary>
      <div className="px-5 pb-5 -mt-1 text-slate-300 text-sm leading-relaxed">
        {a}
      </div>
    </details>
  );
}
